import { useIsMutating } from "@tanstack/react-query";
import { type RefObject, useCallback, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { PRINTOUT_AI_SETTINGS_MUTATION_KEY } from "../../hooks/usePrintoutAiSettings";
import { isPresetSettingKey, withPresetSetting } from "./detailedPresetState";
import type { DownloadType, PrintoutSettings } from "./Header/usePrintout";
import { applyPresetSettings, toPresetSettings } from "./printoutPresets";
import { toPrintoutSettingsPayload } from "./printoutSettingsPayload";
import type { LivePreset } from "./useDetailedPresetState";

type UpdateSetting = <K extends keyof PrintoutSettings>(key: K, value: PrintoutSettings[K], reloadPreview?: boolean) => void;

/** What this hook needs from `useDetailedPresetState`. */
export type SavedPrintoutState = {
	live: LivePreset | null;
	liveRef: RefObject<LivePreset | null>;
	updatePresetSettings: (presetId: string, settings: LivePreset["settings"]) => boolean;
	/** Resolves whether the saves it waited for worked (a failure already showed its own snackbar). */
	flushPendingSave: () => Promise<boolean>;
};

/** Printout 1's own paths, exactly as they were before saved printouts existed. */
export type Printout1Paths = {
	updateSetting: UpdateSetting;
	/** Applies a whole settings object (Annie's answer) to Printout 1. */
	applySettings: (next: PrintoutSettings) => void;
	/** Printout 1's settings as the user-settings API body — what its saves send and its exports log. */
	payload: ReturnType<typeof toPrintoutSettingsPayload>;
	/** Saves Printout 1 now, the way an export always has (its timer cleared, `updateSettings` awaited). */
	saveNow: () => Promise<void>;
};

type Params = {
	selectedType: DownloadType;
	isModalOpen: boolean;
	/** Printout 1's settings, and the ref the modal keeps current ahead of renders. */
	printoutSettings: PrintoutSettings;
	printoutSettingsRef: RefObject<PrintoutSettings>;
	savedPrintouts: SavedPrintoutState;
	printout1: Printout1Paths;
	/** Rebuilds the preview from the settings as they are right now. */
	reloadPreview: () => void;
};

/**
 * Which settings the printout modal is working with, and where changes and exports go.
 *
 * Two settings states: Printout 1 (`printoutSettings`, saved through user settings — the only settings
 * Summary, Spreadsheet and 530 ever use) and, on the Detailed tab only, the saved printout in use. With
 * no saved printout in use every value here IS Printout 1's — the same objects, and the same callbacks
 * underneath — so a user who never saves a printout gets exactly the modal they had before.
 */
export const useActivePrintoutSettings = ({
	selectedType,
	isModalOpen,
	printoutSettings,
	printoutSettingsRef,
	savedPrintouts,
	printout1,
	reloadPreview,
}: Params) => {
	const { t } = useTranslation();
	const { trackError } = useAnalytics();
	const { showSnackbar } = useSnackbar();
	const { live, liveRef, updatePresetSettings, flushPendingSave } = savedPrintouts;
	const { updateSetting, applySettings, payload: printout1Payload, saveNow: savePrintout1Now } = printout1;

	/** Annie is answering; her answer lands on the printout it was asked about, so switching waits. */
	const isLocked = useIsMutating({ mutationKey: PRINTOUT_AI_SETTINGS_MUTATION_KEY }) > 0;
	/** The saved printout this tab prints, or `null` when it prints Printout 1 (always, outside Detailed). */
	const presetInUse = selectedType === "detailed" ? live : null;
	/** What the panel, the preview and every export of this tab use. Printout 1's own object when no saved printout is in use. */
	const activeSettings = useMemo(
		() => (presetInUse ? applyPresetSettings(printoutSettings, presetInUse.settings) : printoutSettings),
		[presetInUse, printoutSettings],
	);
	/** `activeSettings` as of right now, for callbacks that run before the next render (same reason as `printoutSettingsRef`). */
	const resolveActiveSettings = useCallback(
		(type: DownloadType): PrintoutSettings => {
			const current = liveRef.current;
			return type === "detailed" && current ? applyPresetSettings(printoutSettingsRef.current, current.settings) : printoutSettingsRef.current;
		},
		[liveRef, printoutSettingsRef],
	);
	const selectedTypeRef = useRef(selectedType);
	selectedTypeRef.current = selectedType;

	// A different saved printout (or back to Printout 1) is different settings: rebuild the Detailed preview.
	// Never fires for a user without saved printouts — their `livePresetId` is null from start to finish.
	const livePresetId = live ? live.presetId : null;
	const previewedPresetIdRef = useRef(livePresetId);
	useEffect(() => {
		if (previewedPresetIdRef.current === livePresetId) return;
		previewedPresetIdRef.current = livePresetId;
		if (isModalOpen && selectedType === "detailed") reloadPreview();
	}, [livePresetId, isModalOpen, selectedType, reloadPreview]);

	/**
	 * A change made in the panel or the preview, routed to the printout on screen: the saved printout's
	 * Detailed settings when one is in use, otherwise Printout 1 through `updateSetting` exactly as before.
	 * Summary-only settings always go to Printout 1 — a saved printout doesn't carry them.
	 */
	const updateActiveSetting = useCallback(
		// DEFAULT: same as `updateSetting` — a change rebuilds the preview unless the caller already shows it.
		<K extends keyof PrintoutSettings>(key: K, value: PrintoutSettings[K], rebuildPreview = true) => {
			const current = liveRef.current;
			if (selectedTypeRef.current !== "detailed" || !current || !isPresetSettingKey(key)) {
				updateSetting(key, value, rebuildPreview);
				return;
			}
			updatePresetSettings(current.presetId, withPresetSetting(printoutSettingsRef.current, current.settings, key, value));
			if (rebuildPreview) reloadPreview();
		},
		[updateSetting, liveRef, printoutSettingsRef, updatePresetSettings, reloadPreview],
	);

	/**
	 * Annie's settings, applied to the printout they were asked about — `target` is fixed when the panel
	 * renders (`null` = Printout 1), so an answer can never land on a printout picked while it was on its
	 * way. Switching is held while Annie works, so a mismatch shouldn't happen; if it does, the answer is
	 * dropped — and the user told so — rather than written over the wrong printout.
	 */
	const applySettingsTo = useCallback(
		(target: string | null, next: PrintoutSettings) => {
			if (target === null) {
				applySettings(next);
				return;
			}
			if (updatePresetSettings(target, toPresetSettings(next))) {
				reloadPreview();
				return;
			}
			const current = liveRef.current;
			trackError("printout_ai_apply_dropped", { preset_id: target, active_preset_id: current ? current.presetId : null });
			showSnackbar(t("printout.ai.dropped"), "warning");
		},
		[applySettings, updatePresetSettings, reloadPreview, liveRef, trackError, showSnackbar, t],
	);
	const settingsTarget = presetInUse ? presetInUse.presetId : null;
	const applyDetailedSettings = useCallback((next: PrintoutSettings) => applySettingsTo(settingsTarget, next), [applySettingsTo, settingsTarget]);

	/**
	 * Saves the printout about to be exported before the export, so what comes out is what's stored.
	 * Printout 1 does exactly what it always has; a saved printout sends its waiting edit (if any) to
	 * itself and leaves Printout 1's own pending save untouched. Either way the export goes ahead if the
	 * save fails — the failure has already been shown.
	 */
	const saveBeforeExport = async (): Promise<void> => {
		if (presetInUse) {
			await flushPendingSave();
			return;
		}
		await savePrintout1Now();
	};

	/** The product-event fields describing which printout, with which settings, is being exported. */
	const exportedPrintout = () => ({
		presetId: presetInUse ? presetInUse.presetId : null,
		...(presetInUse ? toPrintoutSettingsPayload(activeSettings) : printout1Payload),
	});

	return {
		activeSettings,
		resolveActiveSettings,
		/** The saved printout the Detailed tab is printing; `null` = Printout 1. */
		presetInUse,
		/** The saved printout picked (whatever the tab); `null` = Printout 1. Keys the settings panel. */
		livePresetId,
		isLocked,
		updateActiveSetting,
		applyDetailedSettings,
		saveBeforeExport,
		exportedPrintout,
	};
};
